/// Marketing-site adapter for the per-tenant "which categories are taking applicants" list
/// surfaced by lead-engine. Used by the careers page to grey out closed categories and by the
/// form to disable closed options in the dropdown.
///
/// Resolution order for the lead-engine base URL:
///   1. `OWNLY_LEAD_ENGINE_URL` — explicit override.
///   2. Derived from `OWNLY_RECRUITMENT_WEBHOOK_URL` (strip path).
///   3. Hardcoded `https://app.ownlydigital.co.uk` fallback. The old `app.ownlydigital.online`
///      host still answers and is kept attached, so links in already-sent email keep working.

import * as Sentry from "@sentry/nextjs";

/** Emergency override for recruitment. While this is true the careers page shows every area as
 * closed and the apply form is disabled, no matter what lead-engine reports.
 *
 * It should normally stay false: the switch that matters is per-category, in lead-engine's
 * /admin/jobs, and this constant exists only for the case where that admin cannot be reached.
 * It was true for a few hours on 2026-07-26 because the admin toggles were not reaching this site
 * at all. Now that they are, all six categories are closed at the source instead. */
export const HIRING_PAUSED = false;

export type JobCategory =
  "sales" | "software_dev" | "design" | "marketing" | "ops" | "other";

export const JOB_CATEGORIES: readonly JobCategory[] = [
  "sales",
  "software_dev",
  "design",
  "marketing",
  "ops",
  "other",
];

export const CATEGORY_LABELS: Record<JobCategory, string> = {
  sales: "Sales",
  software_dev: "Software development",
  design: "Design",
  marketing: "Marketing",
  ops: "Operations / admin",
  other: "Other",
};

/** What each job actually involves, in plain words. Shown on the careers cards and under each
 * option in the apply form, so someone can tell which area they fit without guessing. */
export const CATEGORY_BLURBS: Record<JobCategory, string> = {
  sales:
    "Talk to business owners who need a website or software. Book the calls, close the deals, or do both. Paid on commission from money that actually comes in.",
  software_dev:
    "Build the websites and tools we make for clients, from the first screen to the live site. We work in Next.js and TypeScript.",
  design:
    "Design websites for small businesses from start to finish: the look, the layout and the brand.",
  marketing:
    "Help us and our clients win more enquiries through ads, search or content. Tell us which channel you are strongest at.",
  ops: "Keep things running behind the scenes: managing projects, admin, scheduling and bookkeeping.",
  other:
    "Something that does not fit above but you think we would want? Pick this and tell us what you do.",
};

/** The second choice that appears under Sales. Values are the CRM roles they are hired as
 * (lead-engine `setter`, `closer`, `full_cycle`), so the hire step and the contract follow from it.
 *
 * Pay wording is held to what is actually settled (lead-engine `describeCommission`, and the setter
 * terms Tom agreed on 2026-10-02): setters 10% of build fees actually received on deals that
 * complete from calls they booked, nothing on monthly fees. No closer or full cycle rate is fixed,
 * so none is printed. */
export type SalesTrack = "setter" | "closer" | "full_cycle";

export const SALES_TRACKS: readonly {
  value: SalesTrack;
  label: string;
  role: string;
  blurb: string;
}[] = [
  {
    value: "setter",
    label: "Booking calls",
    role: "Appointment setter",
    blurb:
      "Call business owners and book them in for a sales call with us. You earn 10% of the build fee we actually receive on every deal that comes from a call you booked, whoever closes it. Monthly fees are not included.",
  },
  {
    value: "closer",
    label: "Closing deals",
    role: "Closer",
    blurb:
      "Take the sales calls that are booked in for you and win the work. Paid commission on money that actually comes in from the deals you close.",
  },
  {
    value: "full_cycle",
    label: "Both",
    role: "Full cycle",
    blurb:
      "Find the business, book the call and close it yourself. Paid commission on money that actually comes in from the deals you close.",
  },
];

export function isSalesTrack(value: unknown): value is SalesTrack {
  return SALES_TRACKS.some((t) => t.value === value);
}

export function categoryLabel(value: string): string {
  return (
    (CATEGORY_LABELS as Record<string, string>)[value] ??
    value.replace(/_/g, " ")
  );
}

/** Lead Engine's origin, for server-side calls. Shared with the /booked summary route. */
export function leadEngineBaseUrl(): string {
  const explicit = process.env.OWNLY_LEAD_ENGINE_URL?.trim();
  if (explicit) return explicit.replace(/\/$/, "");
  const webhook = process.env.OWNLY_RECRUITMENT_WEBHOOK_URL?.trim();
  if (webhook) {
    try {
      const u = new URL(webhook);
      return `${u.protocol}//${u.host}`;
    } catch {
      /* fall through */
    }
  }
  return "https://app.ownlydigital.co.uk";
}

/** Fetch the list of currently-open job categories. Server-only; safe to call from RSC pages.
 *
 * Failures fall closed — we return nothing rather than advertise roles we could not confirm are
 * open. This used to fall open, which hid a real outage: lead-engine's auth gate redirects
 * /api/public/jobs to /login, fetch follows that to a 200 HTML page, the JSON parse throws, and
 * the page silently showed all six areas as taking applicants regardless of the admin toggles. A
 * page that under-advertises during an outage is recoverable; one that invites applications we are
 * not taking is not. */
export async function fetchOpenJobCategories(): Promise<JobCategory[]> {
  if (HIRING_PAUSED) return [];
  const url = `${leadEngineBaseUrl()}/api/public/jobs`;
  try {
    // Short revalidate window to cut origin load while keeping admin toggles
    // near-real-time (within ~60s). Lead-engine still enforces availability on
    // submit (410 for a category closed during the window), so a briefly-stale
    // page can't sneak a submission through.
    // redirect: "manual" so an auth gate on the other end shows up as a 3xx we can report,
    // instead of being followed to a 200 login page that only fails later at JSON.parse.
    const res = await fetch(url, {
      next: { revalidate: 60 },
      redirect: "manual",
    });
    if (!res.ok) {
      reportBroken(
        `Lead Engine returned ${res.status} for the open-roles list`,
        {
          status: String(res.status),
          url,
        },
      );
      return [];
    }
    const data = (await res.json()) as { openCategories?: string[] };
    const open = Array.isArray(data.openCategories) ? data.openCategories : [];
    return JOB_CATEGORIES.filter((c) => open.includes(c));
  } catch (err) {
    reportBroken("Could not read the open-roles list from Lead Engine", {
      url,
      message: err instanceof Error ? err.message : String(err),
    });
    return [];
  }
}

/** Send the failure somewhere a human will actually see it.
 *
 * This was `console.warn` into Vercel's logs, which is the same as nowhere: the careers page
 * advertised roles that were switched off for months and the only trace was a warning nobody
 * reads. It is a warning rather than an error because the page still renders correctly (closed),
 * but it means recruitment cannot be controlled from the admin until someone looks. */
function reportBroken(message: string, extra: Record<string, string>) {
  console.warn(
    JSON.stringify({ event: "ownly_site.jobs_unavailable", message, ...extra }),
  );
  Sentry.captureMessage(message, {
    level: "warning",
    tags: { feature: "careers", integration: "lead-engine" },
    extra,
  });
}
